import { FunctionComponent, PropsWithChildren } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    Chip,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Field, Formik } from 'formik';
import { BoldLabel, RequiredMark } from '@starjack/shared-components';
import { PriceCondition } from '@/models/conditionLink';
import { FormikTextField } from '@/components/Layout/Form/Field';
import { FormikDirtyGuard } from '@/components/Layout/Form/FormikDirtyGuard';
import { DatePickerField } from '@/components/Layout/Form/DatePickerField';
import { WeekdaySelectField } from '@/components/Layout/Form/WeekdaySelectField';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { MoneyInputField } from '@/components/Layout/Form/MoneyInputField';
import { RadioGroupField } from '@/components/Layout/Form/RadioGroupField';
import { CheckboxField } from '@/components/Layout/Form/CheckboxField';
import {
    ConfigurationIdNameMap,
    ProductPersonNameConfiguration,
} from '@/models/textReplacement';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useGetProductPersonNameConfigurationQuery } from '@/api/v2/textReplacement';
import { DrawerActions } from '@starjack/shared-components';
import { useGetB2BUsersQuery } from '@/api/v2/b2b';
import { AutocompleteField } from '@/components/Layout/Form/AutoCompleteField';
import { FormHeader } from './ConditionList';
import {
    ConditionFormCardContent,
    Hint,
    Row,
    RowInfo,
    SectionTitle,
    TextInputContainer,
    TitleCardContent,
} from './ConditionFormComponents';
import { ProductEnvironmentField } from '@/pages/rulesets/components/conditions/ProductEnvironmentField';

type B2BAutoCompleteValue = {
    label: string;
    id: string;
};

const PriceConditionFormSchema = Yup.object().shape({
    name: Yup.string().required('Interne Bezeichnung ist ein Pflichtfeld'),
});

const numberFieldNullCheck = (value) => {
    if (isNaN(value)) return null;
    else if (value < 0) return null;
    else return value;
};

const getSplitId = (id: string) => {
    if (id.length < 9) return id;

    return `${id.substring(0, 4)} ... ${id.slice(-4)}`;
};

type PriceConditionFormProps = {
    condition: PriceCondition;
    onSave: (condition: PriceCondition) => void;
    onClose: () => void;
    isNewCondition: boolean;
    isLoading?: boolean;
};

export const PriceConditionForm: FunctionComponent<
    PropsWithChildren<PriceConditionFormProps>
> = ({ condition, onClose, onSave, isLoading, isNewCondition }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: b2bUsers } = useGetB2BUsersQuery();
    const { data } = useGetProductPersonNameConfigurationQuery(liftOperatorId);

    return (
        <Box
            sx={{
                width: '860px',
            }}
        >
            <FormHeader
                title={
                    !isNewCondition
                        ? `Regel "${condition.name}" bearbeiten`
                        : 'Neue Regel anlegen'
                }
                subtitle={
                    <>
                        Alle Felder sind optional befüllbar (Ausnahme: „Interne
                        Bezeichnung“).
                        <br />
                        Nur befüllte Felder kommen zur Anwendung und übersteuern
                        die Basiskonfiguration des Tickets, leere Felder werden
                        ignoriert.
                        <br />
                        Es kommt jeweils nur die erste Regel aus jeder Kategorie
                        („Verfügbarkeit“ / „Preis“) zur Anwendung, die auf die
                        Suchanfrage des Gastes passt.
                        <br />
                        Die Abfrage der Regeln erfolgt nach der
                        Speicheranordnung (Priorität), von oben nach unten.
                    </>
                }
            />

            <Formik
                validationSchema={PriceConditionFormSchema}
                validateOnChange={false}
                initialValues={{
                    ...condition,
                    priceChange: {
                        ...condition.priceChange,
                        amount: condition.priceChange?.amount || 0,
                    },
                }}
                onSubmit={(values: PriceCondition) => {
                    // convert some empty values to null
                    onSave({
                        ...values,
                        priceChange: {
                            ...values.priceChange,
                            amount: values.priceChange?.amount || 0,
                        }, // always make it a reduction
                        dayContingentFrom: numberFieldNullCheck(
                            values.dayContingentFrom
                        ),
                        dayContingentTo: numberFieldNullCheck(
                            values.dayContingentTo
                        ),
                        startDateContingentFrom: numberFieldNullCheck(
                            values.startDateContingentFrom
                        ),
                        startDateContingentTo: numberFieldNullCheck(
                            values.startDateContingentTo
                        ),
                        conditionContingentFrom: numberFieldNullCheck(
                            values.conditionContingentFrom
                        ),
                        conditionContingentTo: numberFieldNullCheck(
                            values.conditionContingentTo
                        ),
                        validityPeriodContingentFrom: numberFieldNullCheck(
                            values.validityPeriodContingentFrom
                        ),
                        validityPeriodContingentTo: numberFieldNullCheck(
                            values.validityPeriodContingentTo
                        ),
                        sellingGroup: values.sellingGroup || null,
                        notSellingGroup: values.notSellingGroup || null,
                        roundTo:
                            values.roundTo === undefined ||
                            values.roundTo === null
                                ? null
                                : values.roundTo,
                        userIds: values.userIds || [],
                    });
                }}
            >
                {({ errors, values, setFieldValue, submitForm }) => (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            submitForm();
                        }}
                    >
                        <FormikDirtyGuard />
                        <Stack
                            sx={{
                                p: 6,
                            }}
                            spacing={6}
                        >
                            <Card>
                                <TitleCardContent>
                                    <Typography variant='h6'>
                                        1. Regel
                                    </Typography>
                                </TitleCardContent>

                                <Divider />
                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='name'
                                                    textFieldProps={{
                                                        error: !!errors['name'],
                                                        helperText:
                                                            errors['name'] ||
                                                            '',
                                                        label: (
                                                            <span>
                                                                Interne
                                                                Bezeichnung{' '}
                                                                <RequiredMark />
                                                            </span>
                                                        ),
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='externalName.de'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'externalName'
                                                        ]?.['de'],
                                                        helperText:
                                                            errors[
                                                                'externalName'
                                                            ]?.['de'] || '',
                                                        label: 'Anzeigename Deutsch',
                                                    }}
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='externalName.en'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'externalName.en'
                                                        ],
                                                        helperText:
                                                            errors[
                                                                'externalName.en'
                                                            ] || '',
                                                        label: 'Anzeigename English',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>
                            </Card>

                            <Card>
                                <TitleCardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant='h6'>
                                        2. Bedingung&nbsp;
                                    </Typography>
                                    <Typography>
                                        - wann kommt die Regel zur Anwendung
                                    </Typography>
                                </TitleCardContent>

                                <Divider />

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Für Verkäufe bei B2B/hospitality:
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='sellingGroup'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'sellingGroup'
                                                        ],
                                                        helperText:
                                                            errors[
                                                                'sellingGroup'
                                                            ],
                                                        label: 'hospitality-Verkaufsgruppe einschließen',
                                                    }}
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='notSellingGroup'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'notSellingGroup'
                                                        ],
                                                        helperText:
                                                            errors[
                                                                'notSellingGroup'
                                                            ] || '',
                                                        label: 'hospitality-Verkaufsgruppe ausschließen',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Row>
                                            <TextInputContainer>
                                                <Field name='userIds'>
                                                    {({ field }) => {
                                                        const { value } = field;
                                                        const autoCompleteValues =
                                                            (value || [])
                                                                .map(
                                                                    (
                                                                        id: string
                                                                    ) =>
                                                                        b2bUsers?.b2bUsers.find(
                                                                            (
                                                                                user
                                                                            ) =>
                                                                                user.id ===
                                                                                id
                                                                        )
                                                                )
                                                                .filter(Boolean)
                                                                .map(
                                                                    (user) => ({
                                                                        label: user.name,
                                                                        id: user.id,
                                                                    })
                                                                );

                                                        return (
                                                            <Autocomplete<
                                                                B2BAutoCompleteValue,
                                                                true
                                                            >
                                                                multiple
                                                                value={
                                                                    autoCompleteValues
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    options
                                                                ) => {
                                                                    setFieldValue(
                                                                        'userIds',
                                                                        options.map(
                                                                            (
                                                                                o
                                                                            ) =>
                                                                                o.id
                                                                        )
                                                                    );
                                                                }}
                                                                disablePortal
                                                                options={
                                                                    b2bUsers?.b2bUsers.map(
                                                                        (
                                                                            user
                                                                        ) => ({
                                                                            label: user.name,
                                                                            id: user.id,
                                                                        })
                                                                    ) || []
                                                                }
                                                                sx={{
                                                                    width: '100%',
                                                                }}
                                                                renderInput={(
                                                                    params
                                                                ) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label='B2B-User auswählen'
                                                                    />
                                                                )}
                                                                isOptionEqualToValue={(
                                                                    option,
                                                                    value
                                                                ) =>
                                                                    option.id ===
                                                                    value.id
                                                                }
                                                                renderOption={(
                                                                    props,
                                                                    option
                                                                ) => {
                                                                    const {
                                                                        ...optionProps
                                                                    } = props;
                                                                    return (
                                                                        <Box
                                                                            key={
                                                                                option.id
                                                                            }
                                                                            sx={{
                                                                                borderRadius:
                                                                                    '8px',
                                                                                margin: '5px',
                                                                            }}
                                                                            component='li'
                                                                            {...optionProps}
                                                                        >
                                                                            <Box>
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontWeight:
                                                                                            'bold',
                                                                                        mb: 2,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        option.label
                                                                                    }
                                                                                </Typography>

                                                                                <Typography
                                                                                    sx={{
                                                                                        ml: 1,
                                                                                    }}
                                                                                    variant='subtitle1'
                                                                                >
                                                                                    {getSplitId(
                                                                                        option.id
                                                                                    )}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                </Field>
                                            </TextInputContainer>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Kontingent Zählung
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Ist diese Checkbox aktiviert = die
                                            Kontingentierung zählt für jedes
                                            hinterlegte Produkt separat (auf
                                            Ticketebene).
                                            <br />
                                            Ist diese Checkbox deaktiviert = die
                                            Kontingentierung zählt für alle
                                            hinterlegten Produkte gemeinsam (auf
                                            Ticketebene).
                                        </Hint>
                                        <Row>
                                            <TextInputContainer>
                                                <CheckboxField
                                                    name={
                                                        'contingentsProductIdSpecific'
                                                    }
                                                    label={
                                                        'Produktspezifische Zählung'
                                                    }
                                                />
                                            </TextInputContainer>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Ticket-Gültigkeit (Startdatum)
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Hinweis für{' '}
                                            {renderSeasonTicketsBoldLabel(':')}{' '}
                                            Konfigurationen der Bedingung
                                            „Ticket-Gültigkeit“ werden erst am
                                            hinterlegten Datum im Onlineverkauf
                                            sichtbar (gleich wie bei
                                            „Ticket-Verkaufszeitraum“), da für
                                            diese Tickets im Shop kein
                                            Startdatum auswählbar ist.
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                    mt: 2,
                                                }}
                                            >
                                                Bei Mehrtagestickets wird das
                                                Kontingent nur am ersten Tag
                                                abgezogen = das
                                                Gültigkeitsstartdatum des
                                                Tickets ist relevant
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                    mt: 2,
                                                }}
                                            >
                                                Startkontingent: &quot;1.
                                                Ticket&quot; bitte als
                                                &quot;0&quot; angeben
                                            </Typography>
                                        </Hint>

                                        <Stack>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                }}
                                            >
                                                <TextInputContainer>
                                                    <FormikTextField
                                                        name='startDateContingentFrom'
                                                        textFieldProps={{
                                                            type: 'number',
                                                            label: 'Kontingent pro Startdatum von',
                                                        }}
                                                    />
                                                </TextInputContainer>

                                                <TextInputContainer>
                                                    <FormikTextField
                                                        name='startDateContingentTo'
                                                        textFieldProps={{
                                                            type: 'number',
                                                            label: 'Kontingent pro Startdatum bis',
                                                        }}
                                                    />
                                                </TextInputContainer>
                                            </Box>
                                        </Stack>

                                        <Stack>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    width: '100%',
                                                }}
                                            >
                                                <TextInputContainer>
                                                    <FormikTextField
                                                        name='conditionContingentFrom'
                                                        textFieldProps={{
                                                            type: 'number',
                                                            label: 'Kontingent pro Regel von',
                                                        }}
                                                    />
                                                </TextInputContainer>

                                                <TextInputContainer>
                                                    <FormikTextField
                                                        name='conditionContingentTo'
                                                        textFieldProps={{
                                                            type: 'number',
                                                            label: 'Kontingent pro Regel bis',
                                                        }}
                                                    />
                                                </TextInputContainer>
                                            </Box>
                                        </Stack>

                                        <Stack>
                                            <RowInfo>
                                                Kontingent über den gesamten
                                                Gültigkeitszeitraum (Gültig
                                                ab/bis)
                                            </RowInfo>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    width: '100%',
                                                }}
                                            >
                                                <TextInputContainer>
                                                    <FormikTextField
                                                        name='validityPeriodContingentFrom'
                                                        textFieldProps={{
                                                            type: 'number',
                                                            label: 'Kontingent pro Gültigkeitszeitraum von',
                                                        }}
                                                    />
                                                </TextInputContainer>

                                                <TextInputContainer>
                                                    <FormikTextField
                                                        name='validityPeriodContingentTo'
                                                        textFieldProps={{
                                                            type: 'number',
                                                            label: 'Kontingent pro Gültigkeitszeitraum bis',
                                                        }}
                                                    />
                                                </TextInputContainer>
                                            </Box>
                                        </Stack>

                                        <Stack>
                                            <RowInfo>
                                                Die Zählung des Kontingents
                                                bezieht sich auf die konkrete
                                                Regel
                                            </RowInfo>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    width: '100%',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <TextInputContainer>
                                                    <FormikTextField
                                                        name='minDaysBeforeTicketStart'
                                                        textFieldProps={{
                                                            type: 'number',
                                                            label: 'Regel gilt bis so viele Tage vor dem Ticket-Startdatum',
                                                        }}
                                                    />
                                                </TextInputContainer>
                                            </Box>
                                        </Stack>

                                        <Row>
                                            <TextInputContainer>
                                                <DatePickerField
                                                    name='startDateValidFrom'
                                                    label='Gültig ab'
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <DatePickerField
                                                    name='startDateValidTo'
                                                    label='Gültig bis'
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Stack>
                                            <RowInfo>
                                                In diesem Zeitraum nur gültig an
                                                den Wochentagen:
                                            </RowInfo>
                                            <WeekdaySelectField
                                                name={'startDateValidWeekdays'}
                                            />
                                        </Stack>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Ticket Verkaufszeitraum&nbsp;
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Wenn der Verkaufszeitraum in der
                                            Zukunft liegt, ist die Regel auch
                                            erst im hinterlegten
                                            Verkaufszeitraum im Shop sichtbar!
                                        </Hint>

                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='dayContingentFrom'
                                                    textFieldProps={{
                                                        type: 'number',
                                                        label: 'Kontingent pro Verkaufstag von',
                                                    }}
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='dayContingentTo'
                                                    textFieldProps={{
                                                        type: 'number',
                                                        label: 'Kontingent pro Verkaufstag bis',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <RowInfo>
                                            Bei Mehrtagestickets wird das
                                            Kontingent nur am ersten Tag
                                            abgezogen
                                        </RowInfo>

                                        <Row>
                                            <TextInputContainer>
                                                <DatePickerField
                                                    name={'validFrom'}
                                                    label='Verkaufszeitraum ab'
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <DatePickerField
                                                    name={'validTo'}
                                                    label='Verkaufszeitraum bis'
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Stack>
                                            {' '}
                                            <RowInfo>
                                                In diesem Zeitraum nur gültig an
                                                den Wochentagen:
                                            </RowInfo>
                                            <WeekdaySelectField
                                                name={'validWeekdays'}
                                            />
                                        </Stack>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography variant={'subtitle2'}>
                                        Für folgende Personentypen
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Wähle hier all jene
                                            Personenkategorien, für welche die
                                            Regel zur Anwendung kommen soll.
                                            Hier gelistet sind die
                                            Originalbeschreibungen, wie sie von
                                            SKIDATA an starjack übermittelt
                                            werden:
                                        </Hint>

                                        <Row>
                                            <AutocompleteField
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiAutocomplete-tag': {
                                                        mx: 1,
                                                        my: 2,
                                                    },
                                                }}
                                                name='personTypeIds'
                                                options={data?.persons || []}
                                                getOptionLabel={(
                                                    option: ConfigurationIdNameMap
                                                ) => option.name}
                                                onChange={(options) => {
                                                    setFieldValue(
                                                        'personTypeIds',
                                                        options.map((o) => o.id)
                                                    );
                                                }}
                                                valueTransformHandler={(
                                                    values: string[]
                                                ) => {
                                                    if (data) {
                                                        const { persons } =
                                                            data as ProductPersonNameConfiguration;
                                                        return persons.filter(
                                                            (p) =>
                                                                values.includes(
                                                                    p.id
                                                                )
                                                        );
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label='Personentypen auswählen'
                                                    />
                                                )}
                                                renderTags={(
                                                    value: ConfigurationIdNameMap[],
                                                    getTagProps
                                                ) =>
                                                    value.map(
                                                        (
                                                            option: ConfigurationIdNameMap,
                                                            index: number
                                                        ) => (
                                                            <Chip
                                                                key={index}
                                                                color='primary'
                                                                label={
                                                                    option.name
                                                                }
                                                                {...getTagProps(
                                                                    {
                                                                        index,
                                                                    }
                                                                )}
                                                            />
                                                        )
                                                    )
                                                }
                                            />
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography variant={'subtitle2'}>
                                        Für folgende Umgebungen
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack>
                                        <ProductEnvironmentField
                                            values={values['environments']}
                                            onChange={(environments) => {
                                                setFieldValue(
                                                    'environments',
                                                    environments
                                                );
                                            }}
                                        />
                                    </Stack>
                                </ConditionFormCardContent>
                            </Card>

                            <Card>
                                <TitleCardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant='h6'>
                                        3. Aktion&nbsp;
                                    </Typography>
                                    <Typography>- daraus resultiert</Typography>
                                </TitleCardContent>
                                <Divider />

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Row>
                                            <TextInputContainer>
                                                <CheckboxField
                                                    name='useCatalogPrice'
                                                    label='Katalogpreis verwenden'
                                                />
                                            </TextInputContainer>
                                        </Row>
                                        <Row>
                                            <TextInputContainer>
                                                <MoneyInputField
                                                    name='priceChange'
                                                    label='Rabattwert'
                                                />
                                            </TextInputContainer>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='roundTo'
                                                    textFieldProps={{
                                                        type: 'number',
                                                        label: 'Rundung des reduzierten Ticketpreises auf wie viele Nachkommastellen ',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>
                                        <Row>
                                            <TextInputContainer>
                                                <RadioGroupField
                                                    name='priceChangeAbsolute'
                                                    radios={[
                                                        {
                                                            label: 'Wert in EUR',
                                                            value: true,
                                                        },
                                                        {
                                                            label: 'Wert in Prozent',
                                                            value: false,
                                                        },
                                                    ]}
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <CheckboxField
                                                    name='round50'
                                                    label={'Auf 50 Cent runden'}
                                                />
                                            </TextInputContainer>
                                        </Row>
                                        <Row>
                                            <TextInputContainer>
                                                <CheckboxField
                                                    name={'blockCancellation'}
                                                    label={
                                                        'Ticket nicht stornierbar'
                                                    }
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <CheckboxField
                                                    name={
                                                        'calculateRefundableProductVariant'
                                                    }
                                                    label={
                                                        'Refundable Preis anbieten'
                                                    }
                                                />
                                            </TextInputContainer>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>
                            </Card>

                            {!!Object.keys(errors).length && (
                                <Row
                                    sx={{
                                        ml: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                        }}
                                    >
                                        * Bitte zuerst alle Pflichtfelder
                                        ausfüllen
                                    </Typography>
                                </Row>
                            )}

                            <DrawerActions>
                                <Button onClick={onClose} color='primary'>
                                    Schließen
                                </Button>

                                <LoadingButton
                                    type='submit'
                                    color='primary'
                                    variant='contained'
                                    loading={isLoading}
                                >
                                    Speichern
                                </LoadingButton>
                            </DrawerActions>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

const renderSeasonTicketsBoldLabel = (addOn = '') => (
    <BoldLabel
        sx={{
            fontSize: '0.95rem',
        }}
    >
        Saison-/Jahres- oder Depotkarten{addOn}
    </BoldLabel>
);
